import { useEffect, useMemo } from "react";
import ObjectHeading, { PinnedNotesConfig } from "@/components/ObjectHeading";
import { ProfileSkeleton } from "@/components/Skeleton";
import { useQuery } from "@apollo/client";
import {
  PERSON_DETAILS_HEADER_QUERY,
  PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY,
  PERSON_DETAILS_HEADER_EDUCATION_QUERY,
  PERSON_DETAILS_HEADER_CAREER_QUERY
} from "../../queries";
import { Person, CRMNote, NotePriorityValues, NoteNotableValues } from "@/types";
import { renderName } from "@/lib/personHelpers";
import { Action } from "@/components/MenuButton";
import { usePersonActions } from "../../hooks/usePersonActions";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { withStickyHeader } from "@/components/StickyHeader";
import { PERSON_NOTES_QUERY, PersonNotesQueryResponse } from "@/pages/people/[pgaId]/person/notes/queries";
import { useNavigate, useLocation } from "react-router-dom";
import { useHasSearchParam } from "@/hooks/useHasSearchParam";
import { getNoteParentLink, getNotesPagePath } from "@/features/global/components/NotesTable/noteHelpers";
import { usePhotoboothOptional } from "@/features/photobooth";
import { useCanAccessPhotoboothBool } from "@/hooks/useCapabilities";
import { ccmsSuspensionsForStatus, hasMembershipSuspensionChromeLoaded } from "@/features/person/utils/ccmsSuspensions";
import { usePersonProfileReadCapabilities } from "@/features/person/hooks/usePersonProfileReadCapabilities";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import { m360Paths } from "@/lib/m360Paths";
import { PersonObjectHeadingRefresh } from "./PersonObjectHeadingRefresh";
import {
  buildPersonTags,
  createClassificationInfo,
  createFacilityInfo,
  createProgramAndSectionInfo
} from "./personObjectHeadingHelpers";
/*import { gql } from "@apollo/client";

// TODOQuery to fetch all classifications with descriptions
const CLASSIFICATIONS_QUERY = gql`
  query GetClassifications {
    classifications {
      nodes {
        id
        name
        description
      }
    }
  }
`;*/

type ObjectHeadingWrapper = {
  personData?: { person: Person };
  pgaId: string;
  actionButtons?: Action[];
  menuButtonLabel?: string;
  isDetailsViewDesktop?: boolean;
  showMinimalMetadata?: boolean; // New prop to control metadata display
  avatarSize?: "medium" | "large" | "xlarge"; // New prop to control avatar size (small not used in practice)
  variant?: "complete" | "condensed" | "landing-page"; // New prop to control ObjectHeading variant
  hideTags?: boolean; // New prop to hide status tags
  onImageEditClick?: () => void; // New prop to enable photo editing
  pinnedNotes?: PinnedNotesConfig;
  isSticky?: boolean;
  initialNotesDialogOpen?: boolean;
  /** When true, name/program/facility/job become in-app identity links. Default false so side sheets and other condensed callers do not navigate away. */
  linkIdentityChrome?: boolean;
  /** People `PersonObjectHeading` always sets this. Side sheets leave it off. */
  refreshLayout?: boolean;
  /** Force compact refresh layout (side sheets). Production person profile omits this and uses `useIsCompactLayout`. */
  compact?: boolean;
  /** Person side sheet refresh header without More/Less. */
  sideSheet?: boolean;
  /** Side sheet only: show job/facility in header when Employment section is hidden. */
  showEmploymentInHeader?: boolean;
  showProfileLink?: boolean;
  onOpenProfile?: () => void;
};

type PersonData = { person: Person };

export {
  CLASS_F_TAG_TOOLTIP,
  ClassFTag,
  ProgramStatusTag,
  createClassificationInfo,
  createFacilityInfo,
  createProgramAndSectionInfo
} from "./personObjectHeadingHelpers";

export const ObjectHeadingWrapper = ({
  pgaId,
  actionButtons,
  menuButtonLabel,
  isDetailsViewDesktop,
  personData,
  showMinimalMetadata = false,
  avatarSize,
  variant,
  hideTags = false,
  onImageEditClick,
  pinnedNotes,
  isSticky,
  initialNotesDialogOpen,
  linkIdentityChrome = false,
  refreshLayout = false,
  compact,
  sideSheet,
  showEmploymentInHeader,
  showProfileLink,
  onOpenProfile
}: ObjectHeadingWrapper) => {
  const { canViewPersonMembership, canViewPersonEducation, canViewPersonCareer } = usePersonProfileReadCapabilities();

  const { data, loading, error } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    skip: !!personData,
    errorPolicy: "all" // Don't throw, just return error - let parent handle it
  });

  const skipMembershipQuery =
    !canViewPersonMembership || Boolean(personData?.person && hasMembershipSuspensionChromeLoaded(personData.person));

  const { data: membershipData, loading: membershipLoading } = useQuery<PersonData>(
    PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY,
    {
      variables: { pgaId },
      skip: skipMembershipQuery,
      fetchPolicy: "network-only"
    }
  );

  const skipEducationQuery = !canViewPersonEducation || Boolean(personData?.person?.university);

  const { data: educationData } = useQuery<PersonData>(PERSON_DETAILS_HEADER_EDUCATION_QUERY, {
    variables: { pgaId },
    skip: skipEducationQuery,
    fetchPolicy: "network-only"
  });

  const skipCareerQuery = !canViewPersonCareer || personData?.person?.employments !== undefined;

  const { data: careerData, loading: careerLoading } = useQuery<PersonData>(PERSON_DETAILS_HEADER_CAREER_QUERY, {
    variables: { pgaId },
    skip: skipCareerQuery,
    fetchPolicy: "network-only"
  });

  const membershipChromePending = canViewPersonMembership && !skipMembershipQuery && membershipLoading;
  const careerChromePending = canViewPersonCareer && !skipCareerQuery && careerLoading;

  // TODO: Re-enable after adding proper test mocks
  // const { data: classificationsData } = useQuery<{
  //   classifications: { nodes: Classification[] };
  // }>(CLASSIFICATIONS_QUERY, {
  //   errorPolicy: "ignore",
  //   skip: !(personData || data)
  // });
  const classificationsData /*: { classifications: { nodes: Classification[] } } | undefined */ = undefined;

  const { isMobile } = useIsCompactLayout();

  // For condensed variant or mobile-only (< 768px), automatically show minimal metadata (just PGA ID)
  // Tablets (768-1023px) show full metadata
  const effectiveShowMinimalMetadata = showMinimalMetadata || variant === "condensed" || (!variant && isMobile);

  if (loading || membershipChromePending || careerChromePending) return <ProfileSkeleton />;
  if (error) return null; // Let parent component handle the error

  const basePerson = personData?.person || data!.person;
  const membershipPerson = membershipData?.person;
  const educationPerson = educationData?.person;
  const careerPerson = careerData?.person;
  const person = {
    ...basePerson,
    ...(membershipPerson
      ? {
          latestProgram: membershipPerson.latestProgram,
          openCcmsSuspensions: membershipPerson.openCcmsSuspensions,
          ccmsSuspensions: ccmsSuspensionsForStatus(membershipPerson)
        }
      : {}),
    ...(educationPerson?.university !== undefined ? { university: educationPerson.university } : {}),
    ...(careerPerson?.employments !== undefined ? { employments: careerPerson.employments } : {})
  };

  if (refreshLayout) {
    return (
      <PersonObjectHeadingRefresh
        person={person}
        pgaId={pgaId}
        canViewPersonCareer={canViewPersonCareer}
        actionButtons={actionButtons}
        menuButtonLabel={menuButtonLabel}
        linkIdentityChrome={linkIdentityChrome}
        hideTags={hideTags}
        onImageEditClick={onImageEditClick}
        pinnedNotes={pinnedNotes}
        isSticky={isSticky}
        initialNotesDialogOpen={initialNotesDialogOpen}
        compact={compact}
        sideSheet={sideSheet}
        showEmploymentInHeader={showEmploymentInHeader}
        showProfileLink={showProfileLink}
        onOpenProfile={onOpenProfile}
      />
    );
  }

  // Determine appropriate avatar size based on variant or auto-detection
  const effectiveAvatarSize = avatarSize || "large";
  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;
  const classifications = classificationsData;

  const metaData = effectiveShowMinimalMetadata
    ? [{ text: pgaId, label: "PGA ID", copyable: true }] // Only show PGA ID for condensed variant or minimal metadata
    : [
        ...createProgramAndSectionInfo(person, pgaId, linkIdentityChrome),
        { text: pgaId, label: "PGA ID", copyable: true },
        ...(canViewPersonCareer ? createFacilityInfo(person, linkIdentityChrome) : []),
        ...createClassificationInfo(person, pgaId, classifications, linkIdentityChrome)
      ];

  const title = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);

  return (
    <ObjectHeading
      title={title}
      titleHref={linkIdentityChrome ? m360Paths.personOverview(pgaId) : undefined}
      identityLinkChrome={linkIdentityChrome}
      imageUrl={profilePhoto}
      metaData={metaData}
      iconName="person"
      actions={actionButtons}
      menuButtonLabel={menuButtonLabel}
      isDetailsView
      tag={buildPersonTags(person, hideTags)}
      isDetailsViewDesktop={isDetailsViewDesktop}
      avatarSize={effectiveAvatarSize}
      variant={variant}
      onImageEditClick={onImageEditClick}
      pinnedNotes={pinnedNotes}
      isSticky={isSticky}
      initialNotesDialogOpen={initialNotesDialogOpen}
    />
  );
};

type Props = {
  pgaId: string;
  hideActions?: boolean;
  hideTags?: boolean;
  avatarSize?: "medium" | "large" | "xlarge";
  isSticky?: boolean;
  disableScrollAdjustment?: boolean;
  disableSticky?: boolean;
  onImageEditClick?: () => void;
};

const VISITED_PERSON_PROFILES_KEY = "visitedPersonProfiles";
const STUDENT_NOTABLE_TYPES: string[] = [
  NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT,
  NoteNotableValues.UNIVERSITY_PROBATION
];

// Base PersonObjectHeading component (non-sticky)
function BasePersonObjectHeading({
  pgaId,
  hideActions = false,
  hideTags = false,
  avatarSize,
  isSticky,
  onImageEditClick
}: Props) {
  const { actionButtons, menuButtonLabel, personData } = usePersonActions(pgaId);
  const { isCompactLayout, isMobile } = useIsCompactLayout();
  const { canAccessPeoplePages } = useUniversityStaffPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNoteDeepLink = useHasSearchParam("openNote");
  const isStudentUniversityRoute = /^\/education\/universities\/\d+\/students\//.test(location.pathname);
  const linkIdentityChrome = canAccessPeoplePages && !isStudentUniversityRoute;
  const { canViewPersonNotes } = usePersonProfileReadCapabilities();

  const { data: notesData } = useQuery<PersonNotesQueryResponse>(PERSON_NOTES_QUERY, {
    variables: { pgaId },
    skip: !canViewPersonNotes
  });

  // Active student university only — lastAttended must not feed getNoteParentLink,
  // which builds student-detail URLs that StudentDetailLayout queries with alumni: false.
  const universityLegacyId = personData?.university?.legacyId;

  const viewAllNotesPath = getNotesPagePath(pgaId, location.pathname);
  const studentRouteMatch = location.pathname.match(/^\/education\/universities\/(\d+)\/students\/[^/]+/);

  const filteredNotes = useMemo(
    () =>
      notesData?.personNotes?.nodes?.filter((note) => {
        if (note.priority === "NONE") return false;
        if (studentRouteMatch && !STUDENT_NOTABLE_TYPES.includes(note.notableType)) return false;
        return true;
      }) || [],
    [notesData, studentRouteMatch]
  );

  const hasAlertNotes = useMemo(() => {
    return filteredNotes.some((note) => note.priority === NotePriorityValues.ALERT);
  }, [filteredNotes]);

  const shouldAutoOpen = useMemo(() => {
    if (!hasAlertNotes || !notesData || hasNoteDeepLink) {
      return false;
    }
    try {
      const stored = sessionStorage.getItem(VISITED_PERSON_PROFILES_KEY);
      const visited = stored ? JSON.parse(stored) : [];
      return !visited.includes(pgaId);
    } catch {
      return false;
    }
  }, [hasAlertNotes, notesData, pgaId, hasNoteDeepLink]);

  useEffect(() => {
    if (notesData && shouldAutoOpen) {
      try {
        const stored = sessionStorage.getItem(VISITED_PERSON_PROFILES_KEY);
        const visited = stored ? JSON.parse(stored) : [];
        if (!visited.includes(pgaId)) {
          sessionStorage.setItem(VISITED_PERSON_PROFILES_KEY, JSON.stringify([...visited, pgaId]));
        }
      } catch {
        // Ignore sessionStorage errors
      }
    }
  }, [pgaId, notesData, shouldAutoOpen]);

  const pinnedNotes: PinnedNotesConfig | undefined = canViewPersonNotes
    ? {
        notes: filteredNotes,
        onViewAll: () => navigate(viewAllNotesPath),
        getNoteAction: (note: CRMNote) => {
          const link = getNoteParentLink(note, { pgaId, universityLegacyId });
          return link ? () => navigate(link) : undefined;
        }
      }
    : undefined;

  const photoboothContext = usePhotoboothOptional();
  const canAccessPhotobooth = useCanAccessPhotoboothBool();

  const effectiveOnImageEditClick =
    onImageEditClick ||
    (canAccessPhotobooth && photoboothContext
      ? () => photoboothContext.openPhotoboothByPgaId(pgaId, { source: "person_avatar" })
      : undefined);
  const effectiveHideActions = hideActions || (isMobile && isSticky);
  const effectiveHideTags = hideTags || (isMobile && isSticky);
  const effectiveAvatarSize = avatarSize || (isCompactLayout ? "medium" : "large");
  const visibleActionButtons = effectiveHideActions ? [] : actionButtons;

  return (
    <>
      <div data-testid="person-details-header">
        <ObjectHeadingWrapper
          pgaId={pgaId}
          actionButtons={visibleActionButtons}
          menuButtonLabel={effectiveHideActions ? undefined : menuButtonLabel}
          personData={personData ? { person: personData } : undefined}
          avatarSize={effectiveAvatarSize}
          hideTags={effectiveHideTags}
          onImageEditClick={effectiveOnImageEditClick}
          isSticky={isSticky}
          pinnedNotes={pinnedNotes}
          initialNotesDialogOpen={shouldAutoOpen}
          linkIdentityChrome={linkIdentityChrome}
          refreshLayout
        />
      </div>
    </>
  );
}

export const PERSON_OBJECT_HEADING_COMPACT_NAVBAR_MOBILE_BASE_OFFSET_REM = 7;

const StickyPersonObjectHeading = withStickyHeader(BasePersonObjectHeading, {
  baseOffset: 4.75,
  mobileBaseOffset: PERSON_OBJECT_HEADING_COMPACT_NAVBAR_MOBILE_BASE_OFFSET_REM, // Mobile - below compact TopActionBar + Breadcrumb compact bar
  scrollAdjustment: 0.5,
  backgroundColor: "bg-sys-brand-surface"
});

// Version without scroll adjustment for FocusedPageLayout contexts
const NoScrollAdjustmentPersonObjectHeading = withStickyHeader(BasePersonObjectHeading, {
  baseOffset: 6.6875, // Desktop - below FocusedPageLayout TopAppBar (107px = pt-7[28px] + h-[70px] + pb-2[8px] + border-b[1px])
  mobileBaseOffset: 6.6875, // Mobile - same as desktop for FocusedPageLayout
  scrollAdjustment: 0, // No scroll adjustment to prevent collision
  backgroundColor: "bg-sys-brand-surface"
});

// Wrapper that chooses the appropriate version
function PersonObjectHeadingWrapper(props: Props) {
  const { disableScrollAdjustment, disableSticky, ...componentProps } = props;

  if (disableSticky) {
    return <BasePersonObjectHeading {...componentProps} isSticky={false} />;
  }

  if (disableScrollAdjustment) {
    return <NoScrollAdjustmentPersonObjectHeading {...componentProps} />;
  }

  return <StickyPersonObjectHeading {...componentProps} />;
}

// Export the wrapper as PersonObjectHeading
export const PersonObjectHeading = PersonObjectHeadingWrapper;
